import { Container, Heading, Text, VStack, Box, UnorderedList, ListItem, Link } from '@chakra-ui/react';

export default function Privacy() {
  return (
    <Container maxW="container.lg" py={10}>
      <VStack spacing={8} align="stretch">
        <Heading as="h1" size="xl" textAlign="center">Privacy Policy for Megalith Maps</Heading>
        
        <Text>Last Updated: May 31, 2025</Text>

        <Text>
          Welcome to Megalith Maps! We are committed to protecting your privacy. This Privacy Policy explains how we collect, 
          use, disclose, and safeguard your information when you visit our website and use our services. Please read this 
          privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.
        </Text>

        <Box>
          <Heading as="h2" size="lg" mb={4}>1. Information We Collect</Heading>
          <Text mb={2}>We may collect information about you in a variety of ways. The information we may collect on the Site includes:</Text>
          <UnorderedList spacing={3} pl={4}>
            <ListItem>
              <Text as="strong">Personal Data:</Text> Personally identifiable information, such as your name, email address, and other contact details that you voluntarily give to us when you register with the Site or when you choose to participate in various activities related to the Site, such as online chat and message boards.
            </ListItem>
            <ListItem>
              <Text as="strong">User-Generated Content:</Text> Information you provide when you create or share content, including details about megalithic sites, uploaded media (photos, videos, articles), feature tags, comments, and discussion forum posts. This content may be publicly viewable.
            </ListItem>
            <ListItem>
              <Text as="strong">Usage Data:</Text> Information our servers automatically collect when you access the Site, such as your IP address, your browser type, your operating system, your access times, and the pages you have viewed directly before and after accessing the Site.
            </ListItem>
            <ListItem>
              <Text as="strong">Device and Browser Information:</Text> Information about the computer or mobile device you use to access the Site, including the device model, operating system, and browser type.
            </ListItem>
            <ListItem>
              <Text as="strong">Cookies and Similar Technologies:</Text> We use cookies, web beacons, tracking pixels, and other tracking technologies on the Site to help customize the Site and improve your experience. For more information on how we use cookies, please refer to our Cookie Policy (if applicable, or expand here).
            </ListItem>
            <ListItem>
              <Text as="strong">Location Data for Sites:</Text> We collect geographic coordinates and related location information for the megalithic sites documented on our platform. We do not typically collect your personal real-time location unless you explicitly provide it for a specific feature (e.g., "sites near me," with your consent).
            </ListItem>
          </UnorderedList>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4}>2. How We Use Your Information</Heading>
          <Text mb={2}>Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the Site to:</Text>
          <UnorderedList spacing={2} pl={4}>
            <ListItem>Create and manage your account.</ListItem>
            <ListItem>Provide, operate, and maintain our website and services.</ListItem>
            <ListItem>Display and manage information about megalithic sites, including your contributions.</ListItem>
            <ListItem>Enable users to discover, document, and discuss megalithic sites.</ListItem>
            <ListItem>Facilitate community interaction through discussion forums and other collaborative features.</ListItem>
            <ListItem>Improve our platform, user experience, and develop new features.</ListItem>
            <ListItem>Personalize your experience on the Site (e.g., suggesting relevant content).</ListItem>
            <ListItem>Communicate with you about your account, service updates, or important notices.</ListItem>
            <ListItem>Monitor and analyze usage and trends to improve the Site and services.</ListItem>
            <ListItem>Compile anonymous statistical data and analysis for use internally or with third parties.</ListItem>
            <ListItem>Prevent fraudulent transactions, monitor against theft, and protect against criminal activity.</ListItem>
            <ListItem>Resolve disputes and troubleshoot problems.</ListItem>
            <ListItem>Respond to your comments and questions and provide customer service.</ListItem>
          </UnorderedList>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4}>3. Disclosure of Your Information</Heading>
          <Text mb={2}>We may share information we have collected about you in certain situations. Your information may be disclosed as follows:</Text>
          <UnorderedList spacing={3} pl={4}>
            <ListItem>
              <Text as="strong">By Law or to Protect Rights:</Text> If we believe the release of information about you is necessary to respond to legal process, to investigate or remedy potential violations of our policies, or to protect the rights, property, and safety of others, we may share your information as permitted or required by any applicable law, rule, or regulation.
            </ListItem>
            <ListItem>
              <Text as="strong">Third-Party Service Providers:</Text> We may share your information with third parties that perform services for us or on our behalf, including data analysis, email delivery, hosting services (e.g., Firebase), mapping services (e.g., Google Maps), customer service, and marketing assistance. These third parties are obligated to protect your information.
            </ListItem>
            <ListItem>
              <Text as="strong">Publicly Visible Information:</Text> Certain information you post, such as site details, media, comments, and forum posts, is intended for public consumption and will be visible to other users of the Site and the public. Your profile information may also be visible depending on your settings.
            </ListItem>
            <ListItem>
              <Text as="strong">Business Transfers:</Text> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
            </ListItem>
            <ListItem>
              <Text as="strong">Aggregated or Anonymized Data:</Text> We may share aggregated or anonymized information that does not directly identify you with third parties for research, analysis, and other purposes.
            </ListItem>
            <ListItem>
              <Text as="strong">With Your Consent:</Text> We may disclose your personal information for any other purpose with your consent.
            </ListItem>
          </UnorderedList>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4}>4. Data Security</Heading>
          <Text mb={4}>
            We use administrative, technical, and physical security measures to help protect your personal information. 
            While we have taken reasonable steps to secure the personal information you provide to us, please be aware that 
            despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission 
            over the Internet can be guaranteed against any interception or other type of misuse.
          </Text>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4}>5. Data Retention</Heading>
          <Text mb={4}>
            We will only keep your personal information for as long as it is necessary for the purposes set out in this 
            privacy policy, unless a longer retention period is required or permitted by law (such as tax, accounting, 
            or other legal requirements). For example, account information is retained as long as your account is active. 
            User-generated content may be retained indefinitely as part of the platform's historical record, unless you request its deletion.
          </Text>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4}>6. Your Rights</Heading>
          <Text mb={2}>Depending on your location, you may have the following rights regarding your personal information:</Text>
          <UnorderedList spacing={2} pl={4}>
            <ListItem>The right to access – You have the right to request copies of your personal data.</ListItem>
            <ListItem>The right to rectification – You have the right to request that we correct any information you believe is inaccurate or complete information you believe is incomplete.</ListItem>
            <ListItem>The right to erasure – You have the right to request that we erase your personal data, under certain conditions.</ListItem>
            <ListItem>The right to restrict processing – You have the right to request that we restrict the processing of your personal data, under certain conditions.</ListItem>
            <ListItem>The right to object to processing – You have the right to object to our processing of your personal data, under certain conditions.</ListItem>
            <ListItem>The right to data portability – You have the right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.</ListItem>
            <ListItem>The right to opt-out of marketing communications – You can opt-out of receiving marketing emails from us by following the unsubscribe link in such emails.</ListItem>
          </UnorderedList>
          <Text mt={2}>If you wish to exercise any of these rights, please contact us using the contact information below.</Text>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4}>7. Children's Privacy</Heading>
          <Text mb={4}>
            Our services are not directed to individuals under the age of 13 (or a higher age threshold as required by applicable law). 
            We do not knowingly collect personal information from children. If we become aware that a child has provided us with 
            personal information, we will take steps to delete such information. If you become aware that your child has provided 
            us with personal information, please contact us.
          </Text>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4}>8. International Data Transfers</Heading>
          <Text mb={4}>
            Your information, including personal data, may be transferred to — and maintained on — computers located outside 
            of your state, province, country, or other governmental jurisdiction where the data protection laws may differ 
            from those in your jurisdiction. If you are located outside the United States and choose to provide information to us, 
            please note that we transfer the data, including personal data, to the United States and process it there. 
            Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.
          </Text>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4}>9. Changes to This Privacy Policy</Heading>
          <Text mb={4}>
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
            Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy 
            periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
          </Text>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4}>10. Contact Us</Heading>
          <Text>
            If you have any questions about this Privacy Policy, please contact us through our <Link href="/contact" color="teal.500" isExternal>Contact Page</Link> or by email at admin@megalithmaps.com.
          </Text>
        </Box>
      </VStack>
    </Container>
  );
}
